import type { ReactNode } from 'react'

import { DetailReading } from '../detail-rows'
import type { ChannelWeek, DetailRow, LineBlock } from '../line-groups'
import { settledAt, tradingPeriod } from './report-format'
import { ReportTable } from './report-table'
import type { ReportColumn, ReportRow } from './report-table'

/**
 * The details reports: invoice lines in blocks, printed in the same grid as the
 * rest of the pack.
 *
 * Rows are built apart from the table so a screen can page them — a month of
 * lines is thousands of rows, and the table only ever sees one page of them.
 */

/* -------------------------------------------------------------------------- */
/* Item details                                                               */
/* -------------------------------------------------------------------------- */

const QUANTITY: ReportColumn<DetailRow> = {
  id: 'quantity',
  header: 'Sold',
  format: 'quantity',
  value: (row) => row.quantity,
}

const COMPED: ReportColumn<DetailRow> = {
  id: 'compQuantity',
  header: 'Comped',
  format: 'quantity',
  value: (row) => row.compQuantity,
  hideWhenEmpty: true,
}

const NET: ReportColumn<DetailRow> = {
  id: 'net',
  header: 'Net',
  format: 'usd',
  value: (row) => row.net,
}

const PROFIT: ReportColumn<DetailRow>[] = [
  // A line sold without a cost prints a dash, never $0.00 and a 100% margin.
  { id: 'cost', header: 'Cost', format: 'usd', value: (row) => row.cost },
  { id: 'margin', header: 'Margin', format: 'usd', value: (row) => row.margin },
  { id: 'marginPercent', header: 'Margin %', format: 'share', value: (row) => row.marginPercent },
]

/** Where a line was sold, who sold it and to whom — minus whichever is the block. */
function whereColumns(omit: LineBlock | null): ReportColumn<DetailRow>[] {
  return [
    {
      id: 'outlet',
      header: 'Outlet',
      value: (row) => row.branchName,
      visibleWhen: () => omit !== 'location',
    },
    {
      id: 'cashier',
      header: 'Cashier',
      value: (row) => row.cashierName,
      visibleWhen: () => omit !== 'employee',
    },
    {
      id: 'customer',
      header: 'Customer',
      value: (row) => row.customerName,
      visibleWhen: () => omit !== 'customer',
    },
  ]
}

function itemDetailColumns(
  reading: DetailReading,
  block: LineBlock | null,
): ReportColumn<DetailRow>[] {
  const columns: ReportColumn<DetailRow>[] = [
    {
      id: 'invoice',
      header: 'Invoice',
      value: (row) => row.invoiceNumber ?? row.orderNumber,
    },
    { id: 'date', header: 'Date', value: (row) => settledAt(row.at) },
  ]

  if (reading === DetailReading.Profit) return [...columns, QUANTITY, NET, ...PROFIT]

  const money: ReportColumn<DetailRow>[] = [
    { id: 'total', header: 'Total', format: 'usd', value: (row) => row.totalAmount },
    {
      id: 'discount',
      header: 'Discount',
      format: 'usd',
      value: (row) => row.discount,
      hideWhenEmpty: true,
    },
  ]

  return [...columns, ...whereColumns(block), QUANTITY, COMPED, ...money, NET]
}

interface ItemDetailTableProps {
  ariaLabel: string
  rows: ReportRow<DetailRow>[]
  /** The period's total row — pass only when the rows are everything, not a search's matches. */
  total?: DetailRow
  reading: DetailReading
  block: LineBlock | null
  footer?: ReactNode
}

export function ItemDetailTable({
  ariaLabel,
  rows,
  total,
  reading,
  block,
  footer,
}: ItemDetailTableProps) {
  return (
    <ReportTable
      ariaLabel={ariaLabel}
      columns={itemDetailColumns(reading, block)}
      rows={
        total ? [...rows, { id: 'total', data: total, label: 'All items', tone: 'total' }] : rows
      }
      footer={footer}
    />
  )
}

/* -------------------------------------------------------------------------- */
/* Sales by channel                                                           */
/* -------------------------------------------------------------------------- */

const CHANNEL_COLUMNS: ReportColumn<ChannelWeek>[] = [
  {
    id: 'week',
    header: 'Week of',
    value: (row) => row.week,
    render: (row) => tradingPeriod(row.week),
  },
  { id: 'posInvoices', header: 'POS invoices', format: 'whole', value: (row) => row.posInvoices },
  { id: 'posNet', header: 'POS net', format: 'usd', value: (row) => row.posNet },
  { id: 'qrInvoices', header: 'QR invoices', format: 'whole', value: (row) => row.qrInvoices },
  { id: 'qrNet', header: 'QR net', format: 'usd', value: (row) => row.qrNet },
  { id: 'invoices', header: 'Invoices', format: 'whole', value: (row) => row.invoices },
  { id: 'net', header: 'Net', format: 'usd', value: (row) => row.net },
  { id: 'qrShare', header: 'QR share', format: 'share', value: (row) => row.qrShare },
]

export function ChannelTable({
  weeks,
  total,
  footer,
}: {
  weeks: ChannelWeek[]
  total: ChannelWeek
  footer?: ReactNode
}) {
  const rows: ReportRow<ChannelWeek>[] = [
    ...weeks.map((week) => ({ id: week.week ?? 'week', data: week })),
    { id: 'total', data: total, label: 'All weeks', tone: 'total' as const },
  ]

  return (
    <ReportTable
      ariaLabel="Sales by channel"
      columns={CHANNEL_COLUMNS}
      rows={rows}
      footer={footer}
    />
  )
}
